import { CONTAINER_TYPE_OPTIONS, type Incoterms, PortTypes } from 'api';
import {
  AddressAutocomplete,
  CurrencySelectField,
  IncotermSelectField,
  PortAutocomplete,
  SelectField,
  TradeCompanyAutocomplete,
  TradeCompanySiteSelector,
  useFormContext,
} from 'form';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Grid, Typography } from 'ui';

import { useTransitTimeEstimator, useTransportEstimator } from '../../hooks';
import type { FormValues } from '../../pages/DealProfitabilityCalculator/DealProfitabilityCalculator';
import { areCoordinatesSetOnSite, isCollectionFromPort } from '../../utils';
import { TransportCostDetails } from '../TransportCostDetails/TransportCostDetails';

export const LogisticsSection = () => {
  const { t } = useTranslation('orders');
  const form = useFormContext<FormValues>();
  const [isAddressDisabled, setIsAddressDisabled] = useState(false);
  const { watch, setValue, setError } = form;
  const { estimateTransportPrice } = useTransportEstimator();
  const { estimateTransitTime } = useTransitTimeEstimator();
  const watchSeller = watch('seller') || undefined;
  const watchBuyer = watch('buyer') || undefined;

  const estimateTransportAndTransitTime = () => {
    estimateTransportPrice();
    estimateTransitTime();
  };
  return (
    <>
      <Typography variant="h5">{t('profitabilityCalculator.logistics.title')}</Typography>
      <Grid container spacing={2}>
        <Grid
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              my: 1,
            }}
          >
            {t('profitabilityCalculator.logistics.sections.supply')}
          </Typography>
          <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
            <Grid size={12}>
              <TradeCompanyAutocomplete name="seller" label={t('profitabilityCalculator.logistics.labels.seller')} />
            </Grid>
            <Grid size={12}>
              <TradeCompanySiteSelector
                name="seller_site"
                label={t('profitabilityCalculator.logistics.labels.sellerSite')}
                tradeCompanyId={watchSeller}
                onSelectOption={(value) => {
                  if (!areCoordinatesSetOnSite(value)) {
                    setError('transport_price', {
                      message: t('profitabilityCalculator.logistics.errors.invalidCoordinatesForSellerSite'),
                    });
                    return;
                  }
                  setValue('origin_address', {
                    country_code: value.country,
                    full_address: value.full_address,
                    latitude: Number.parseFloat(value.latitude || '0'),
                    longitude: Number.parseFloat(value.longitude || '0'),
                  });
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid size={12}>
              <AddressAutocomplete
                name="origin_address"
                label={t('profitabilityCalculator.logistics.labels.originAddress')}
                onAddressSelected={estimateTransportAndTransitTime}
              />
            </Grid>
            <Grid size={12}>
              <PortAutocomplete
                name="port_of_loading"
                label={t('profitabilityCalculator.logistics.labels.portOfLoading')}
                type={PortTypes.REG}
                onSelectOption={(event) => {
                  setValue('origin_port_country', event?.country);
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid size={12}>
              <IncotermSelectField
                name="supply_incoterm"
                label={t('profitabilityCalculator.logistics.labels.incoterm')}
                required={false}
                fullWidth
                onChange={estimateTransportAndTransitTime}
              />
            </Grid>
            <Grid size={12}>
              <SelectField
                label={t('profitabilityCalculator.logistics.labels.containerType')}
                name="container_type"
                options={CONTAINER_TYPE_OPTIONS}
                onChange={estimateTransportAndTransitTime}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              my: 1,
            }}
          >
            {t('profitabilityCalculator.logistics.sections.demand')}
          </Typography>
          <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
            <Grid size={12}>
              <TradeCompanyAutocomplete
                name="buyer"
                label={t('profitabilityCalculator.logistics.labels.buyer')}
                disabled={isAddressDisabled}
              />
            </Grid>
            <Grid size={12}>
              <TradeCompanySiteSelector
                name="buyer_site"
                label={t('profitabilityCalculator.logistics.labels.buyerSite')}
                tradeCompanyId={watchBuyer}
                disabled={isAddressDisabled}
                onSelectOption={(value) => {
                  if (!areCoordinatesSetOnSite(value)) {
                    setError('transport_price', {
                      message: t('profitabilityCalculator.logistics.errors.invalidCoordinatesForBuyerSite'),
                    });
                    return;
                  }
                  setValue('destination_address', {
                    country_code: value.country,
                    full_address: value.full_address,
                    latitude: Number.parseFloat(value.latitude || '0'),
                    longitude: Number.parseFloat(value.longitude || '0'),
                  });
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid size={12}>
              <AddressAutocomplete
                name="destination_address"
                label={t('profitabilityCalculator.logistics.labels.destinationAddress')}
                disabled={isAddressDisabled}
                onAddressSelected={estimateTransportAndTransitTime}
              />
            </Grid>
            <Grid size={12}>
              <PortAutocomplete
                name="port_of_discharge"
                label={t('profitabilityCalculator.logistics.labels.portOfDischarge')}
                type={PortTypes.REG}
                onSelectOption={(event) => {
                  setValue('destination_port_country', event?.country);
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
            <Grid size={12}>
              <IncotermSelectField
                name="demand_incoterm"
                label={t('profitabilityCalculator.logistics.labels.incoterm')}
                required={false}
                fullWidth
                onChange={(event) => {
                  if (isCollectionFromPort(event.target.value as Incoterms)) {
                    setIsAddressDisabled(true);
                  } else {
                    setIsAddressDisabled(false);
                  }
                  estimateTransportAndTransitTime();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <CurrencySelectField
              label={t('profitabilityCalculator.logistics.labels.transportCostCurrency')}
              name="transport_cost_currency"
              onChange={() => estimateTransportAndTransitTime()}
            />
          </Grid>
        </Grid>
        <Grid
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <TransportCostDetails />
        </Grid>
      </Grid>
    </>
  );
};
